import * as React from "react";
import { Box, CircularProgress, Container, Link, Paper, Typography } from "@mui/material";
import { connect } from "react-redux";
import { useParams, Link as RouterLink } from "react-router-dom";
import { notification } from "antd";
import { getRequest, postRequest } from "../../core/network";
import endpoints from "../../constants/endPoints.json";
import { marketingColors, marketingFonts } from "../../app/components/tickitMarketingTheme";
import HeroTemplate from "./templates/HeroTemplate";
import SplitTemplate from "./templates/SplitTemplate";
import MinimalTemplate from "./templates/MinimalTemplate";
import RegistrationPaymentStep from "./RegistrationPaymentStep";
import { getBrandingColors, brandedTextSx } from "./brandingUtils";
import { cardSx, layout } from "./templateLayout";

const TEMPLATES = {
  hero: HeroTemplate,
  split: SplitTemplate,
  minimal: MinimalTemplate,
};

function RegistrationPagePublic({ token, user }) {
  const { slug } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(null);
  const [event, setEvent] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(null);

  const userToken = token?.startsWith("User ") ? token : null;
  const loggedInUser = userToken ? user : null;
  const branding = page?.branding || {};
  const { primary, accent } = getBrandingColors(branding);

  React.useEffect(() => {
    setLoading(true);
    getRequest({ slug }, null, null, endpoints.guest.registrationPage)
      .then((res) => {
        setPage(res.data.page);
        setEvent(res.data.event);
        setError(null);
      })
      .catch(() => setError("Registration page not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (creation) => {
    setSubmitting(true);
    try {
      const res = await postRequest(
        { slug, creation },
        null,
        null,
        userToken,
        endpoints.guest.registrationRequest
      );
      setSuccess({
        autoAccepted: res.data.autoAccepted,
        ticketSent: res.data.ticketSent,
        ticketId: res.data.ticketId,
        needsPayment: res.data.needsPayment,
        paymentMethod: res.data.paymentMethod,
        paymentAddress: res.data.paymentAddress,
        requestId: res.data.requestId,
        email: creation.userInfo.email,
      });
    } catch (err) {
      notification.error({
        message: "Registration failed",
        description: err?.response?.data?.message || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          px: layout.pagePx,
        }}
      >
        <CircularProgress sx={{ color: primary }} />
      </Box>
    );
  }

  if (error || !page || !event) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          px: layout.pagePx,
          py: layout.pagePy,
        }}
      >
        <Paper sx={{ ...cardSx(branding), p: layout.cardPadding, textAlign: "center", maxWidth: 420, width: "100%" }}>
          <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 700, mb: 1, ...brandedTextSx(accent) }}>
            Page not found
          </Typography>
          <Typography sx={{ ...brandedTextSx(accent), opacity: 0.85 }}>
            This registration link may be invalid or no longer active.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (success) {
    if (success.autoAccepted && success.needsPayment) {
      return (
        <Box sx={{ py: layout.pagePy, px: layout.pagePx, bgcolor: marketingColors.gray50, minHeight: "60vh" }}>
          <Container maxWidth="md" disableGutters>
            <RegistrationPaymentStep
              requestId={success.requestId}
              email={success.email}
              paymentMethod={success.paymentMethod}
              paymentAddress={success.paymentAddress}
              branding={branding}
            />
          </Container>
        </Box>
      );
    }

    let message = "Your request has been submitted. The organizer will review it shortly.";
    if (success.autoAccepted && success.ticketSent) {
      message = "You're registered! Check your email for your ticket.";
    } else if (success.autoAccepted) {
      message = "Your request was approved.";
    }
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          px: layout.pagePx,
          py: layout.pagePy,
          bgcolor: marketingColors.gray50,
        }}
      >
        <Paper sx={{ ...cardSx(branding), p: layout.cardPadding, textAlign: "center", maxWidth: 480, width: "100%" }}>
          <Typography
            sx={{
              fontFamily: marketingFonts.display,
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "1.25rem", sm: "1.35rem" },
              ...brandedTextSx(accent),
            }}
          >
            Thank you!
          </Typography>
          <Typography sx={{ ...brandedTextSx(accent), opacity: 0.85, mb: 2 }}>{message}</Typography>
          {success.ticketId && (
            <Link component={RouterLink} to={`/download/${success.ticketId}`} sx={{ color: primary, fontWeight: 600 }}>
              Download ticket
            </Link>
          )}
        </Paper>
      </Box>
    );
  }

  const Template = TEMPLATES[page.template] || HeroTemplate;

  return (
    <Box sx={{ bgcolor: marketingColors.gray50 }}>
      {!loggedInUser && (
        <Box sx={{ textAlign: "center", py: 1.25, px: layout.pagePx, bgcolor: `${accent}12` }}>
          <Typography variant="body2" sx={{ ...brandedTextSx(accent), fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}>
            Have an account?{" "}
            <Link component={RouterLink} to={`/Login?redirect=/register/${slug}`} sx={{ color: primary, fontWeight: 600 }}>
              Sign in
            </Link>{" "}
            to use your saved details.
          </Typography>
        </Box>
      )}
      <Template
        page={page}
        event={event}
        user={loggedInUser}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  token: state?.auth?.token,
  user: state?.auth?.user,
});

export default connect(mapStateToProps)(RegistrationPagePublic);
